import React from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Tooltip} from "@mui/material";
import {CheckCircleOutline, LockOutlined} from "@mui/icons-material";
import {observer} from "mobx-react-lite";
import {useNavigate} from "react-router";
import {useStores} from "../../../stores";
import type {ComputedOpportunity} from "../briefingLogic";
import {
    confidenceState,
    findAssumption,
    FIX_FACT_RECORD,
    formatDayMonth,
    offerFoundationWorklist,
} from "../briefingLogic";
import type {ConfidenceDimension, Fact} from "../briefingTypes";
import {BRIEFING} from "../briefingData";
import {isDimensionParked, markFixPending, useBriefing} from "../briefingState";
import {resolveFixPath} from "../fixRoutes";
import {AssumptionEditor} from "./AssumptionEditor";

export function sourceLabels(sourceSystem: string): string {
    return sourceSystem.split(',')
        .map(id => BRIEFING.tenant.source_systems.find(s => s.id === id.trim())?.label ?? id.trim())
        .join(', ');
}

type ItemRowProps = {
    computed: ComputedOpportunity
    cls: ConfidenceDimension
    item: Fact
    returnPath: string
};

/**
 * One evidence item with its claim, source line and the exact affordance the
 * edit matrix allows: a deep link into the owning page, an override that
 * requires a reason, a lock with its reason, or "Ask {owner}" — never more.
 */
export const FactRow: React.FC<ItemRowProps> = observer(({computed, cls, item, returnPath}) => {
    const {state, dispatch} = useBriefing();
    const {bagStore} = useStores();
    const navigate = useNavigate();
    const [editingAssumption, setEditingAssumption] = React.useState(false);
    const [overriding, setOverriding] = React.useState(false);
    const [overrideReason, setOverrideReason] = React.useState('');

    const itemKey = `${computed.opportunity.id}:${cls}:${item.record_id}`;
    const overridden = state.factOverrides[itemKey];

    const renderAffordance = () => {
        if (item.locked_reason) {
            return (
                <Tooltip title={item.locked_reason}>
                    <span className="db-fact-locked"><LockOutlined sx={{fontSize: 13}}/> Locked</span>
                </Tooltip>
            );
        }
        if (!item.fix) return null;

        // Forward class: never an edit control — route to the owner instead.
        if (item.fix.label === 'Ask owner') {
            const asked = state.askedOwners.includes(itemKey);
            return asked
                ? <span className="db-fact-parked">Waiting on {item.owner} — confidence parked</span>
                : <Button size="small" className="db-fact-action" onClick={() => dispatch({type: 'askOwner', itemKey})}>
                    Ask {item.owner}
                </Button>;
        }

        const affordances: React.ReactNode[] = [];

        // Judgments: override with a required reason (the audit trail).
        if (item.edit_type === 'override_with_reason') {
            if (item.assumption_ref && findAssumption(item.assumption_ref)) {
                affordances.push(
                    <Button key="override" size="small" className="db-fact-action" onClick={() => setEditingAssumption(true)}>
                        Override — requires a reason
                    </Button>
                );
            } else if (overridden) {
                affordances.push(
                    <span key="overridden" className="db-fact-overridden">
                        <CheckCircleOutline sx={{fontSize: 13}}/> Overridden by {BRIEFING.session.user.name} — “{overridden.reason}”
                    </span>
                );
            } else {
                affordances.push(
                    <Button key="override" size="small" className="db-fact-action" onClick={() => {
                        setOverrideReason('');
                        setOverriding(true);
                    }}>
                        Override — requires a reason
                    </Button>
                );
            }
        }

        const path = resolveFixPath(item.fix, bagStore.bag?.id, returnPath);
        if (path) {
            const scripted = item.record_id.startsWith(FIX_FACT_RECORD);
            affordances.push(
                <Button
                    key="fix"
                    size="small"
                    className="db-fact-action"
                    onClick={() => {
                        if (scripted) markFixPending();
                        navigate(path);
                    }}
                >
                    {item.fix.label}
                </Button>
            );
        } else if (affordances.length === 0) {
            affordances.push(
                <Tooltip key="na" title="This page is not part of the showcase build">
                    <span className="db-fact-unavailable">{item.fix.label}</span>
                </Tooltip>
            );
        }
        return affordances;
    };

    const assumption = item.assumption_ref ? findAssumption(item.assumption_ref)?.assumption : undefined;

    return (
        <div className="db-fact-item">
            <div className="db-fact-claim">
                {item.claim_supported}
                {(item.confirmation_state === 'human_confirmed' || overridden) &&
                    <span className="db-fact-confirmed">
                        <CheckCircleOutline sx={{fontSize: 13}}/> Confirmed · {overridden ? BRIEFING.session.user.name : item.owner}
                    </span>
                }
            </div>
            <div className="db-fact-meta">
                {sourceLabels(item.source_system)} · <span className="db-fact-record">{item.record_id}</span>
                {' '}· {formatDayMonth(item.freshness)} · {item.owner}
            </div>
            <div className="db-fact-affordance">{renderAffordance()}</div>

            {assumption &&
                <AssumptionEditor assumption={assumption} open={editingAssumption} onClose={() => setEditingAssumption(false)}/>
            }

            <Dialog open={overriding} onClose={() => setOverriding(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{fontSize: 16, fontWeight: 600}}>Override this judgment</DialogTitle>
                <DialogContent>
                    <div className="db-pop-note">
                        The reason is stored as the audit trail and travels with the move into the initiative and into Finance validation.
                    </div>
                    <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        label="Reason (required)"
                        value={overrideReason}
                        onChange={e => setOverrideReason(e.target.value)}
                        sx={{mt: 1.5}}
                    />
                </DialogContent>
                <DialogActions>
                    <Button size="small" onClick={() => setOverriding(false)}>Cancel</Button>
                    <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        disabled={!overrideReason.trim()}
                        onClick={() => {
                            dispatch({type: 'overrideFact', itemKey, reason: overrideReason.trim()});
                            setOverriding(false);
                        }}
                    >
                        Save override
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
});

type SectionProps = {
    computed: ComputedOpportunity
    cls: ConfidenceDimension
    returnPath: string
};

/** One confidence dimension as a full section: state, scoring checklist, then the facts behind it. */
export const DimensionSection: React.FC<SectionProps> = observer(({computed, cls, returnPath}) => {
    const {state} = useBriefing();
    const meta = BRIEFING.confidence_model.dimensions.find(c => c.id === cls);
    const value = computed.dimensionConfidences[cls];
    const isLowest = value === computed.confidence;
    const parked = isDimensionParked(state, computed.opportunity.id, cls);
    const checklist = computed.checklists[cls];

    return (
        <section className="db-fact-section" aria-label={`${meta?.label ?? cls} evidence`}>
            <div className="db-fact-section-head">
                <span className="db-fact-section-label">
                    {meta?.label ?? cls}
                    <span className="db-dim-question">{meta?.question}</span>
                </span>
                <span className="db-dim-value">
                    {parked
                        ? <span className="db-fact-parked">Parked — waiting on an answer</span>
                        : <>
                            {confidenceState(value)} · {value}%
                            {isLowest && <span className="db-min-tag">sets the opportunity</span>}
                        </>}
                </span>
            </div>
            {checklist.length > 0 &&
                <div className="db-checklist db-fact-checklist" aria-label={`${meta?.label ?? cls} scoring`}>
                    {checklist.map(c =>
                        <span key={c.id} className={"db-check-row" + (c.deduction > 0 ? " unmet" : "")}>
                            <span className={"db-check-points" + (c.deduction === 0 ? " met" : "")}>
                                {c.deduction === 0 ? '✓' : `−${c.deduction}`}
                            </span>
                            {c.label}{c.note ? <span className="db-check-note"> — {c.note}</span> : null}
                        </span>
                    )}
                </div>
            }
            {computed.opportunity.confidence[cls].facts.map(item =>
                <FactRow key={item.record_id} computed={computed} cls={cls} item={item} returnPath={returnPath}/>
            )}
        </section>
    );
});

/** The batch offer when several classes are weak at once (fixture batch_alternative). */
export const WorklistOffer: React.FC<{computed: ComputedOpportunity}> = ({computed}) => {
    if (!offerFoundationWorklist(computed)) return null;
    const weakCount = Object.values(computed.dimensionConfidences).filter(v => v < 80).length;
    return (
        <div className="db-worklist-offer">
            {weakCount} dimensions need confirmation — one sequenced batch beats {weakCount} separate trips.
            <Tooltip title="The foundation worklist page is not part of the showcase build">
                <span className="db-fact-unavailable">Open foundation worklist</span>
            </Tooltip>
        </div>
    );
};
